import React, {useEffect, useState, useCallback} from 'react';
import {View, ScrollView, StyleSheet, TouchableOpacity} from 'react-native';
import {Appbar, TextInput, Button, Snackbar, Text} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation} from '@react-navigation/native';
import {useConfigStore} from '../../store/useConfigStore';
import {useOrgUnitStore} from '../../store/useOrgUnitStore';
import OrgUnitSelector from '../../components/common/OrgUnitSelector';
import {colors, spacing} from '../../theme';

const OrderSettingsScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const {fetchByType, getProperty, updateProperties, isLoading} = useConfigStore();
  const {orgUnits, selectedOrgUnitId, fetchOrgUnits} = useOrgUnitStore();

  const [prefix, setPrefix] = useState('');
  const [serialStart, setSerialStart] = useState('');
  const [deliveryDays, setDeliveryDays] = useState('');
  const [measurementUnit, setMeasurementUnit] = useState('INCHES');
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  // Ensure org units are loaded
  useEffect(() => {
    if (orgUnits.length === 0) {
      fetchOrgUnits();
    }
  }, [orgUnits.length, fetchOrgUnits]);

  // Determine effective orgUnitId: use selected, or auto-select if only one store
  const effectiveOrgUnitId = orgUnits.length === 1 ? orgUnits[0].id : selectedOrgUnitId;

  useEffect(() => {
    if (effectiveOrgUnitId) {
      fetchByType('ORDER', effectiveOrgUnitId);
    }
  }, [fetchByType, effectiveOrgUnitId]);

  useEffect(() => {
    if (effectiveOrgUnitId) {
      setPrefix(getProperty('ORDER', 'ORDER_PREFIX', effectiveOrgUnitId) || 'ORD');
      setSerialStart(getProperty('ORDER', 'ORDER_SERIAL_START', effectiveOrgUnitId) || '1');
      setDeliveryDays(getProperty('ORDER', 'DELIVERY_DAYS', effectiveOrgUnitId) || '4');
      setMeasurementUnit(getProperty('ORDER', 'MEASUREMENT_UNIT', effectiveOrgUnitId) || 'INCHES');
    }
  }, [getProperty, effectiveOrgUnitId, useConfigStore.getState().properties]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await updateProperties('ORDER', [
        {code: 'ORDER_PREFIX', name: 'Order Prefix', value: prefix},
        {code: 'ORDER_SERIAL_START', name: 'Order Serial Start', value: serialStart},
        {code: 'DELIVERY_DAYS', name: 'Delivery Days', value: deliveryDays},
        {code: 'MEASUREMENT_UNIT', name: 'Measurement Unit', value: measurementUnit},
      ], effectiveOrgUnitId);
      setSnackbar(t('config.saved'));
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  }, [prefix, serialStart, deliveryDays, measurementUnit, updateProperties, effectiveOrgUnitId, t]);

  const storeRequired = orgUnits.length > 1 && !selectedOrgUnitId;

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction onPress={() => navigation.goBack()} color={colors.white} />
        <Appbar.Content title={t('config.orderSettings')} titleStyle={styles.appbarTitle} />
        <OrgUnitSelector />
      </Appbar.Header>

      {storeRequired ? (
        <View style={styles.emptyContainer}>
          <Text variant="bodyLarge" style={styles.emptyText}>
            {t('config.selectStorePrompt', {defaultValue: 'Please select a store to configure order settings.'})}
          </Text>
        </View>
      ) : (
        <ScrollView style={styles.content}>
          <Text variant="labelLarge" style={styles.sectionTitle}>ORDER NUMBER</Text>
          <Text variant="bodySmall" style={styles.description}>{t('config.orderPrefixDesc')}</Text>
          <View style={styles.row}>
            <TextInput
              label={t('config.orderPrefix')}
              value={prefix}
              onChangeText={setPrefix}
              mode="outlined"
              style={[styles.input, styles.halfInput]}
            />
            <TextInput
              label={t('config.serialNumber')}
              value={serialStart}
              onChangeText={setSerialStart}
              mode="outlined"
              keyboardType="numeric"
              style={[styles.input, styles.halfInput]}
            />
          </View>
          <Text variant="bodySmall" style={styles.description}>{t('config.serialNumberDesc')}</Text>

          <Text variant="labelLarge" style={[styles.sectionTitle, {marginTop: spacing.lg}]}>
            {t('config.deliveryDays').toUpperCase()}
          </Text>
          <Text variant="bodySmall" style={styles.description}>{t('config.deliveryDaysDesc')}</Text>
          <TextInput
            label={t('config.deliveryDays')}
            value={deliveryDays}
            onChangeText={setDeliveryDays}
            mode="outlined"
            keyboardType="numeric"
            style={styles.input}
          />

          <Text variant="labelLarge" style={[styles.sectionTitle, {marginTop: spacing.lg}]}>
            MEASUREMENT UNIT
          </Text>
          <Text variant="bodySmall" style={styles.description}>
            Unit used for capturing all measurements (body & dress).
          </Text>
          <View style={styles.unitToggleRow}>
            {['INCHES', 'CMS'].map(unit => (
              <TouchableOpacity
                key={unit}
                style={[styles.unitOption, measurementUnit === unit && styles.unitOptionSelected]}
                onPress={() => setMeasurementUnit(unit)}
                activeOpacity={0.7}>
                <Text style={[styles.unitOptionText, measurementUnit === unit && styles.unitOptionTextSelected]}>
                  {unit === 'INCHES' ? 'Inches' : 'Cms'}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          <Button
            mode="contained"
            onPress={handleSave}
            loading={saving}
            disabled={saving || isLoading}
            style={styles.saveBtn}
            buttonColor={colors.primary}>
            {t('common.save')}
          </Button>
        </ScrollView>
      )}

      <Snackbar visible={!!snackbar} onDismiss={() => setSnackbar('')} duration={2000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  content: {padding: spacing.md},
  sectionTitle: {color: colors.textSecondary, marginBottom: spacing.xs},
  description: {color: colors.textSecondary, marginBottom: spacing.sm},
  row: {flexDirection: 'row', gap: spacing.sm},
  input: {marginBottom: spacing.sm, backgroundColor: colors.surface},
  halfInput: {flex: 1},
  unitToggleRow: {flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.sm},
  unitOption: {
    flex: 1, paddingVertical: spacing.sm, borderRadius: 8,
    borderWidth: 1.5, borderColor: colors.border, alignItems: 'center',
    backgroundColor: colors.surface,
  },
  unitOptionSelected: {borderColor: colors.primary, backgroundColor: colors.primary + '15'},
  unitOptionText: {fontSize: 15, fontWeight: '500', color: colors.textSecondary},
  unitOptionTextSelected: {color: colors.primary, fontWeight: '700'},
  saveBtn: {marginTop: spacing.md},
  emptyContainer: {flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xl},
  emptyText: {color: colors.textSecondary, textAlign: 'center'},
});

export default OrderSettingsScreen;
